import { create } from 'zustand';
import type { FeatureKey, EffectiveTier } from '../constants/planLimits';
import { normalizeTier } from '../constants/planLimits';

/**
 * Global "you need to upgrade for this" modal state.
 *
 * Two ways to open it:
 *
 *   1. Implicitly via the API interceptor — any 4xx that includes
 *      `{ feature, requiredPlan }` in the JSON body fires this. Means
 *      every existing fetch site (including the dozens of inline ones)
 *      gets the modal for free.
 *
 *   2. Imperatively via `openUpgradeGate()` — for components that want
 *      to nudge the user BEFORE they trigger an API call (e.g. the
 *      cloaking toggle in Builder, the "Add custom domain" button).
 *
 * Why a global store instead of context: the interceptor lives outside
 * the React tree (it patches window.fetch at boot). A store lets it
 * publish events without needing a Provider in the path.
 */

export interface UpgradeGateContext {
  /** Stable feature key from PLAN_LIMITS.features (e.g. 'cloaking'). */
  feature: FeatureKey | string;
  /** Lowest tier that unlocks the feature. */
  requiredPlan: EffectiveTier;
  /** User's current tier when the gate fired (drives the "from → to" UI). */
  currentPlan: EffectiveTier;
  /**
   * Optional human-readable message override. Use the i18n
   * `upgradeGate.feature.<feature>.title` first; fall back to this if
   * the feature key is unknown to the locale file.
   */
  message?: string;
  /** Optional path to redirect to on Upgrade click. Defaults to /upgrade. */
  upgradeUrl?: string;
}

interface UpgradeGateState {
  isOpen: boolean;
  context: UpgradeGateContext | null;
  open: (ctx: UpgradeGateContext) => void;
  close: () => void;
}

export const useUpgradeGateStore = create<UpgradeGateState>((set, get) => ({
  isOpen: false,
  context: null,
  open: (ctx) => {
    // Idempotent: don't re-open / restack the modal if it's already
    // showing for the same feature. Common case: a screen fires three
    // parallel API calls that all 403 with the same feature → we want
    // ONE modal, not three.
    const current = get();
    if (
      current.isOpen &&
      current.context?.feature === ctx.feature &&
      current.context?.requiredPlan === ctx.requiredPlan
    ) {
      return;
    }
    set({
      isOpen: true,
      context: {
        ...ctx,
        currentPlan: normalizeTier(ctx.currentPlan),
        requiredPlan: normalizeTier(ctx.requiredPlan),
      },
    });
  },
  close: () => set({ isOpen: false }),
}));

/**
 * Imperative helper for components that want to show the gate without
 * waiting for the server to 403 (e.g. clicking a disabled toggle).
 *
 *   import { openUpgradeGate } from '../store/upgradeGateStore';
 *   openUpgradeGate({
 *     feature: 'cloaking',
 *     requiredPlan: 'agency',
 *     currentPlan: useEffectivePlan(), // workspace owner plan, not personal
 *   });
 */
export function openUpgradeGate(
  ctx: Omit<UpgradeGateContext, 'currentPlan'> & {
    currentPlan: string | null | undefined;
  }
): void {
  useUpgradeGateStore.getState().open({
    ...ctx,
    currentPlan: normalizeTier(ctx.currentPlan),
  });
}

export function closeUpgradeGate(): void {
  useUpgradeGateStore.getState().close();
}
